import React, { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { Grid, Form, Input, Button, Dropdown } from "semantic-ui-react";
import { userApi } from "@api/users/user";
import { AuditService } from "@api/audit/audit";
import { collegeProgramApi } from "@api/colleges/colleges";
import FacultyLibraryIdSection from "./FacultyLibraryIdSection";

const STYLES = {
  label: {
    color: "#333",
    fontWeight: "bold",
    marginBottom: "0.5rem",
    display: "block",
  },
  input: {
    width: "100%",
  },
  button: {
    backgroundColor: "#225745",
    color: "white",
  },
  buttonContainer: {
    textAlign: "right",
    marginTop: "2rem",
  },
};

const FORM_FIELDS = [
  {
    name: "email",
    label: "Email Address *",
    type: "input",
    icon: "mail",
    placeholder: "librarian@test.ch",
    required: true,
    disabled: true,
  },
  {
    name: "username",
    label: "Username",
    type: "input",
    icon: "user",
    placeholder: "librarian",
    required: false,
  },
  {
    name: "full_name",
    label: "Full Name",
    type: "input",
    icon: "user outline",
    placeholder: "Hector Nabu",
    required: false,
  },
  {
    name: "contact_number",
    label: "Contact Number",
    type: "input",
    icon: "phone",
    placeholder: "Contact Number",
    required: false,
  },
];

// Faculty-specific fields
const FACULTY_FIELDS = [
  {
    name: "department",
    label: "Department",
    type: "dropdown",
    icon: "building",
    placeholder: "Select Department",
    required: false,
  },
  {
    name: "program",
    label: "Program",
    type: "dropdown",
    icon: "graduation cap",
    placeholder: "Select Program",
    required: false,
  },
];

// Utility function to check if user is faculty based on userType
const isFacultyUser = (user) => {
  if (!user) return false;

  // Check userType array
  if (user.userType && Array.isArray(user.userType)) {
    return user.userType.some(
      (userType) => userType?.user_type?.toLowerCase() === "faculty"
    );
  }

  if (
    user.userType &&
    typeof user.userType === "object" &&
    !Array.isArray(user.userType)
  ) {
    return user.userType.user_type?.toLowerCase() === "faculty";
  }

  if (user.roles && Array.isArray(user.roles)) {
    return user.roles.some((role) =>
      typeof role === "string"
        ? role.toLowerCase() === "faculty"
        : role?.name?.toLowerCase() === "faculty"
    );
  }

  return false;
};

const LibrarianAdminProfileForm = ({ user, onSubmit }) => {
  const [formData, setFormData] = useState({
    email: user?.email || "",
    username: user?.username || "",
    full_name: user?.full_name || user?.fullName || "",
    contact_number: user?.contact_number || user?.contactNumber || "",
    library_id: user?.library_id || "",
    department: user?.department || "",
    program: user?.program || "",
  });

  const [dropdownOptions, setDropdownOptions] = useState({
    department: [],
    program: [],
  });

  const [collegesWithPrograms, setCollegesWithPrograms] = useState([]);
  const [isLoadingDropdowns, setIsLoadingDropdowns] = useState(false);

  const isMountedRef = useRef(true);
  const hasLoadedCollegesRef = useRef(false);

  // Cleanup function
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Fetch dropdown options (colleges and programs)
  useEffect(() => {
    if (hasLoadedCollegesRef.current) {
      return;
    }

    const fetchDropdownOptions = async () => {
      setIsLoadingDropdowns(true);
      try {
        const collegesResponse =
          await collegeProgramApi.listCollegesWithPrograms();
        const colleges = collegesResponse.data.data;

        if (isMountedRef.current) {
          setCollegesWithPrograms(colleges);
          hasLoadedCollegesRef.current = true;

          const departmentOptions = colleges.map((college) => ({
            key: college.id,
            value: college.name,
            text: college.name,
          }));

          setDropdownOptions((prev) => ({
            ...prev,
            department: departmentOptions,
          }));
        }
      } catch (error) {
        console.error("Failed to fetch colleges and programs:", error);
      } finally {
        if (isMountedRef.current) {
          setIsLoadingDropdowns(false);
        }
      }
    };

    fetchDropdownOptions();
  }, []);

  // Handle user's existing department/program mapping when colleges are loaded
  useEffect(() => {
    if (!hasLoadedCollegesRef.current || collegesWithPrograms.length === 0) {
      return;
    }

    // Handle user's existing department mapping
    if (user?.department) {
      const departmentCollege = collegesWithPrograms.find(
        (c) => c.name === user.department
      );

      if (departmentCollege?.programs?.length > 0) {
        const programOptions = departmentCollege.programs.map((program) => ({
          key: program.id,
          value: program.name,
          text: program.name,
        }));

        setDropdownOptions((prev) => ({
          ...prev,
          program: programOptions,
        }));
      }
    }
  }, [collegesWithPrograms, user?.department]);

  useEffect(() => {
    const fetchCompleteUserData = async () => {
      try {
        // First, get current user to obtain their ID
        const currentUserResponse = await userApi.get();
        const currentUser = currentUserResponse?.data || currentUserResponse;

        if (currentUser?.id) {
          // Then fetch complete profile using getUserProfileById
          const profileResponse = await userApi.getUserProfileById(
            currentUser.id
          );
          const completeUser = profileResponse?.data || profileResponse;

          if (isMountedRef.current) {
            setFormData({
              email: completeUser.email || "",
              username: completeUser.username || "",
              full_name: completeUser.full_name || completeUser.fullName || "",
              contact_number:
                completeUser.contact_number || completeUser.contactNumber || "",
              library_id: completeUser.library_id || "",
              department: completeUser.department || "",
              program: completeUser.program || "",
            });
          }
        } else {
          console.warn("No user ID found, falling back to prop data");
          fallbackToPropData();
        }
      } catch (error) {
        fallbackToPropData();
      }
    };

    const fallbackToPropData = () => {
      if (user && Object.keys(user).length > 0 && isMountedRef.current) {
        setFormData({
          email: user.email || "",
          username: user.username || "",
          full_name: user.full_name || user.fullName || "",
          contact_number: user.contact_number || user.contactNumber || "",
          library_id: user.library_id || "",
          department: user.department || "",
          program: user.program || "",
        });
      }
    };

    fetchCompleteUserData();
  }, [user]);

  const handleInputChange = useCallback(
    (name, value) => {
      if (isMountedRef.current) {
        setFormData((prev) => ({
          ...prev,
          [name]: value,
        }));

        // If the department dropdown changes, update the program dropdown options
        if (name === "department") {
          const selectedCollege = collegesWithPrograms.find(
            (college) => college.name === value
          );

          if (selectedCollege?.programs?.length > 0) {
            const programOptions = selectedCollege.programs.map((program) => ({
              key: program.id,
              value: program.name,
              text: program.name,
            }));

            setDropdownOptions((prev) => ({
              ...prev,
              program: programOptions,
            }));
          } else {
            setDropdownOptions((prev) => ({
              ...prev,
              program: [],
            }));
          }

          // Reset the program value when department changes
          setFormData((prev) => ({
            ...prev,
            program: "",
          }));
        }
      }
    },
    [collegesWithPrograms]
  );

  // Handle library ID update from the separate component
  const handleLibraryIdUpdate = useCallback(
    async (newLibraryId) => {
      const completeUserData = {
        library_id: newLibraryId,
        username: formData.username || "",
        full_name: formData.full_name || "",
        email: formData.email || "",
        contact_number: formData.contact_number || "",
        department: formData.department || "",
        program: formData.program || "",
        year: user?.year || "",
        semester: user?.semester || "",
        roles: user?.roles || [],
      };

      await onSubmit(completeUserData);

      // Update local form data to keep it in sync
      if (isMountedRef.current) {
        setFormData((prev) => ({
          ...prev,
          library_id: newLibraryId,
        }));
      }
    },
    [formData, onSubmit, user]
  );

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      const userResponse = await userApi.get();
      const currentUser = userResponse?.data || userResponse;
      const userName = currentUser.full_name || "Unknown User";
      const userRole = (currentUser.roles && currentUser.roles[0]) || "patron";

      const updatedFields = [];

      // Check regular form fields
      FORM_FIELDS.forEach((field) => {
        const fieldName = field.name;
        let oldValue = "";

        // Map form field names to API property names (handle both formats)
        switch (fieldName) {
          case "full_name":
            oldValue = user?.full_name || user?.fullName || "";
            break;
          case "contact_number":
            oldValue = user?.contact_number || user?.contactNumber || "";
            break;
          default:
            oldValue = user?.[fieldName] || "";
        }

        const newValue = formData?.[fieldName] || "";
        if (oldValue !== newValue) {
          updatedFields.push(
            `<li><strong>${field.label}:</strong> "${oldValue}" → "${newValue}"</li>`
          );
        }
      });

      // Check faculty-specific fields if user is faculty
      const userIsFaculty = isFacultyUser(user);
      if (userIsFaculty) {
        FACULTY_FIELDS.forEach((field) => {
          const fieldName = field.name;
          const oldValue = user?.[fieldName] || "";
          const newValue = formData?.[fieldName] || "";

          if (oldValue !== newValue) {
            updatedFields.push(
              `<li><strong>${field.label}:</strong> "${oldValue}" → "${newValue}"</li>`
            );
          }
        });
      }

      const action = "Update Librarian Profile";
      const details =
        updatedFields.length > 0
          ? `The user ${userName} (${userRole}) updated their librarian profile at ${new Date().toLocaleString()} with the following changes:<ul>${updatedFields.join(
              ""
            )}</ul>`
          : `The user ${userName} (${userRole}) updated their librarian profile at ${new Date().toLocaleString()} without any field changes.`;

      await AuditService.api.createLog({
        action,
        details,
        created_by: `${userName} (${userRole})`,
      });

      onSubmit(formData);
    },
    [formData, onSubmit, user]
  );

  const renderFormField = useCallback(
    (field) => {
      const { name, label, type, icon, placeholder, required, disabled } =
        field;
      const fieldValue = formData[name] || "";

      return (
        <Form.Field key={name}>
          <label style={STYLES.label}>{label}</label>
          {type === "dropdown" ? (
            <div style={{ position: "relative" }}>
              <Dropdown
                placeholder={placeholder}
                fluid
                selection
                search
                options={dropdownOptions[name] || []}
                value={fieldValue}
                onChange={(e, { value }) => handleInputChange(name, value)}
                style={{
                  width: "100%",
                  paddingLeft: "2.67142857em",
                }}
                noResultsMessage={
                  isLoadingDropdowns
                    ? "Loading..."
                    : (dropdownOptions[name] || []).length === 0
                    ? "No options available"
                    : "No results found"
                }
                disabled={
                  disabled ||
                  isLoadingDropdowns ||
                  (name === "program" && !formData.department)
                }
                loading={isLoadingDropdowns}
              />
              <i
                className={`${icon} icon`}
                style={{
                  position: "absolute",
                  left: "1em",
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 3,
                  pointerEvents: "none",
                  opacity: 0.87,
                }}
              />
            </div>
          ) : (
            <Input
              icon={icon}
              iconPosition="left"
              placeholder={placeholder}
              value={fieldValue}
              onChange={(e) => handleInputChange(name, e.target.value)}
              style={STYLES.input}
              required={required}
              disabled={disabled}
            />
          )}
        </Form.Field>
      );
    },
    [formData, handleInputChange, dropdownOptions, isLoadingDropdowns]
  );

  // Check if user is faculty
  const userIsFaculty = isFacultyUser(user);

  return (
    <Form onSubmit={handleSubmit}>
      <Grid stackable>
        <Grid.Row>
          <Grid.Column width={8}>
            {renderFormField(FORM_FIELDS[0])} {/* Email */}
          </Grid.Column>
          <Grid.Column width={8}>
            {renderFormField(FORM_FIELDS[1])} {/* Username */}
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={8}>
            {renderFormField(FORM_FIELDS[2])} {/* Full Name */}
          </Grid.Column>
          <Grid.Column width={8}>
            {renderFormField(FORM_FIELDS[3])} {/* Contact Number */}
          </Grid.Column>
        </Grid.Row>

        {/* Faculty-specific fields */}
        {userIsFaculty && (
          <Grid.Row>
            <Grid.Column width={8}>
              {renderFormField(FACULTY_FIELDS[0])} {/* Department */}
            </Grid.Column>
            <Grid.Column width={8}>
              {renderFormField(FACULTY_FIELDS[1])} {/* Program */}
            </Grid.Column>
          </Grid.Row>
        )}

        {/* Add Faculty Library ID Component if user is faculty */}
        {userIsFaculty && (
          <FacultyLibraryIdSection
            user={user}
            initialLibraryId={formData.library_id}
            onLibraryIdUpdate={handleLibraryIdUpdate}
          />
        )}
      </Grid>

      <div style={STYLES.buttonContainer}>
        <Button
          type="submit"
          color="green"
          size="medium"
          icon="checkmark"
          content="Update Profile"
          style={STYLES.button}
          disabled={isLoadingDropdowns}
        />
      </div>
    </Form>
  );
};

LibrarianAdminProfileForm.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.string,
    email: PropTypes.string,
    username: PropTypes.string,
    roles: PropTypes.array,
    locationPid: PropTypes.string,
    userType: PropTypes.array,
    department: PropTypes.string,
    program: PropTypes.string,
    year: PropTypes.string,
    semester: PropTypes.string,
    library_id: PropTypes.string,
    full_name: PropTypes.string,
    fullName: PropTypes.string,
    contact_number: PropTypes.string,
    contactNumber: PropTypes.string,
  }),
  onSubmit: PropTypes.func.isRequired,
};

LibrarianAdminProfileForm.defaultProps = {
  user: {},
};

export default LibrarianAdminProfileForm;
